import type { ApolloClient, ErrorLike } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { DocumentNode } from 'graphql';

import { Kitchen } from '../libs';
import {
  BATCH_TRACKING_ACCOUNT_ACTIVITY_QUERY,
  BATCH_TRACKING_BATCHES_QUERY,
  BATCH_TRACKING_BATCH_QUERY,
  BATCH_TRACKING_HOME_BATCHES_QUERY,
  BATCH_TRACKING_TRACKABLE_ITEMS_QUERY
} from '../libs/graphQL/batchTracking';
import { LIST_FOOD_COMPONENTS_BY_NAME_QUERY, LIST_INGREDIENTS_BY_NAME_QUERY } from '../libs/graphQL/queries';
import { getAppSyncErrorType } from '../libs/utils/getAppSyncErrorType';
import { Batch, BatchAccountActivity, BatchDetails, BatchPage, ItemOption, TrackableItem } from '../views/BatchTracking/types';

/** The kitchen days an account activity read covers, `yyyy-MM-dd` in the kitchen's zone, both inclusive. */
export interface ActivityWindow {
  from: string;
  to: string;
}

/** Every batch tracking read lives under `['batchTracking', kitchen, ...]`, so a kitchen's data can be reached by its prefix. */
export const batchTrackingKeys = {
  items: (kitchen: Kitchen) => ['batchTracking', kitchen, 'items'] as const,
  home: (kitchen: Kitchen, day?: string) => ['batchTracking', kitchen, 'home', ...(day === undefined ? [] : [day])] as const,
  batches: (kitchen: Kitchen, filter?: BatchFilter) =>
    ['batchTracking', kitchen, 'batches', ...(filter === undefined ? [] : [filter])] as const,
  batch: (kitchen: Kitchen, id?: string) => ['batchTracking', kitchen, 'batch', ...(id === undefined ? [] : [id])] as const,
  accountActivity: (kitchen: Kitchen, days?: ActivityWindow) =>
    ['batchTracking', kitchen, 'accountActivity', ...(days === undefined ? [] : [days])] as const,
  itemOptions: (kitchen: Kitchen, source: 'ingredients' | 'components', text: string) =>
    ['batchTracking', kitchen, 'itemOptions', source, text] as const
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** How long each read counts as fresh. The catalog changes rarely; Home also polls, since a head chef sees other people's batches. */
const FRESHNESS = {
  items: { staleTime: 30 * MINUTE_MS, gcTime: 2 * HOUR_MS, refetchInterval: 30 * MINUTE_MS, refetchIntervalInBackground: false },
  home: { staleTime: MINUTE_MS, refetchInterval: MINUTE_MS },
  batches: { staleTime: MINUTE_MS },
  accountActivity: { staleTime: MINUTE_MS, refetchInterval: MINUTE_MS, refetchIntervalInBackground: false },
  batch: { staleTime: MINUTE_MS },
  itemOptions: { staleTime: MINUTE_MS }
} as const;

/** Reads through the app's Apollo client, so its auth links apply, but keeps nothing in Apollo's cache: react-query holds the data. */
const read = async <T>(client: ApolloClient, query: DocumentNode, variables: object): Promise<T> => {
  const { data } = await client.query<T>({ query, variables, fetchPolicy: 'no-cache' });
  return data!;
};

export interface BatchFilter {
  /** First day of the range, `yyyy-MM-dd` in the kitchen's zone. */
  from: string;
  /** Last day of the range, inclusive. */
  to: string;
  itemId?: string | undefined;
  /** Only the creations of this account (a `BatchAccountActivity` account's ID); head chefs only. */
  accountId?: string | undefined;
  limit?: number | undefined;
}

export const trackableItemsOptions = (client: ApolloClient, kitchen: Kitchen) =>
  queryOptions({
    queryKey: batchTrackingKeys.items(kitchen),
    queryFn: async () => {
      const { listTrackableItems } = await read<{ listTrackableItems: TrackableItem[] }>(
        client,
        BATCH_TRACKING_TRACKABLE_ITEMS_QUERY,
        { input: { kitchen } }
      );
      return listTrackableItems;
    },
    ...FRESHNESS.items
  });

export const homeBatchesOptions = (client: ApolloClient, kitchen: Kitchen, day: string) =>
  queryOptions({
    queryKey: batchTrackingKeys.home(kitchen, day),
    queryFn: async () => {
      const { listHomeBatchesV2 } = await read<{ listHomeBatchesV2: Batch[] }>(client, BATCH_TRACKING_HOME_BATCHES_QUERY, {
        kitchen,
        day
      });
      return listHomeBatchesV2;
    },
    ...FRESHNESS.home
  });

export const batchesOptions = (client: ApolloClient, kitchen: Kitchen, filter: BatchFilter) =>
  infiniteQueryOptions({
    queryKey: batchTrackingKeys.batches(kitchen, filter),
    queryFn: async ({ pageParam }) => {
      const { listBatchesV2 } = await read<{ listBatchesV2: BatchPage }>(client, BATCH_TRACKING_BATCHES_QUERY, {
        kitchen,
        ...filter,
        next: pageParam
      });
      return listBatchesV2;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    ...FRESHNESS.batches
  });

export const batchOptions = (client: ApolloClient, kitchen: Kitchen, id: string) =>
  queryOptions({
    queryKey: batchTrackingKeys.batch(kitchen, id),
    queryFn: async () => {
      const { getBatchV2 } = await read<{ getBatchV2: BatchDetails }>(client, BATCH_TRACKING_BATCH_QUERY, { kitchen, id });
      return getBatchV2;
    },
    ...FRESHNESS.batch
  });

export const accountActivityOptions = (client: ApolloClient, kitchen: Kitchen, days: ActivityWindow) =>
  queryOptions({
    queryKey: batchTrackingKeys.accountActivity(kitchen, days),
    queryFn: async () => {
      const { getBatchAccountActivityV2 } = await read<{ getBatchAccountActivityV2: BatchAccountActivity }>(
        client,
        BATCH_TRACKING_ACCOUNT_ACTIVITY_QUERY,
        { kitchen, ...days }
      );
      return getBatchAccountActivityV2;
    },
    ...FRESHNESS.accountActivity
  });

/** Any recorded batch moves who has recorded, so a scan, which may register a batch, reads it again too. */
export const invalidateAccountActivity = (queryClient: QueryClient, kitchen: Kitchen): Promise<unknown> =>
  queryClient.invalidateQueries({ queryKey: batchTrackingKeys.accountActivity(kitchen) });

/** A change to a batch moves Home, the Batch Tracking list, every batch page and the account activity of the kitchen; the catalog is untouched. */
export const invalidateBatches = (queryClient: QueryClient, kitchen: Kitchen): Promise<unknown> =>
  Promise.all([
    ...[batchTrackingKeys.home(kitchen), batchTrackingKeys.batches(kitchen), batchTrackingKeys.batch(kitchen)].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    ),
    invalidateAccountActivity(queryClient, kitchen)
  ]);

interface ItemRow {
  id: string;
  name: { en: string };
  measurementUnit?: string | null;
}

const MODELED_UNITS = new Set(['g', 'ml', 'piece']);
const COMPONENT_PAGE_SIZE = 50;
export const ITEM_SEARCH_DELAY_MS = 250;

const toOptions = (rows: ItemRow[] | undefined): ItemOption[] =>
  (rows ?? []).flatMap(({ id, name, measurementUnit }) =>
    measurementUnit && MODELED_UNITS.has(measurementUnit) ? [{ id, name: name.en, unit: measurementUnit }] : []
  );

export interface UseBatchTrackingItemOptionsParams {
  kitchen: Kitchen;
  /** The name to search for; the first page of each source only. */
  text: string;
}

/**
 * Items from `LIST_INGREDIENTS_BY_NAME_QUERY` and `LIST_FOOD_COMPONENTS_BY_NAME_QUERY` (`brand: 'CALO'`, `limit: 50`),
 * keeping the rows measured in g, ml or piece and showing `name.en`.
 */
export interface UseBatchTrackingItemOptionsResult {
  items: ItemOption[];
  loading: boolean;
  error: ErrorLike | undefined;
}

export const useBatchTrackingItemOptions = ({
  kitchen,
  text
}: UseBatchTrackingItemOptionsParams): UseBatchTrackingItemOptionsResult => {
  const client = useApolloClient();
  const ingredients = useQuery({
    queryKey: batchTrackingKeys.itemOptions(kitchen, 'ingredients', text),
    queryFn: () =>
      read<{ listIngredientsByName: { data: ItemRow[] } }>(client, LIST_INGREDIENTS_BY_NAME_QUERY, { kitchen, name: text }),
    ...FRESHNESS.itemOptions
  });
  const components = useQuery({
    queryKey: batchTrackingKeys.itemOptions(kitchen, 'components', text),
    queryFn: () =>
      read<{ listFoodComponentsByName: { data: ItemRow[] } }>(client, LIST_FOOD_COMPONENTS_BY_NAME_QUERY, {
        kitchen,
        brand: 'CALO',
        name: text,
        limit: COMPONENT_PAGE_SIZE
      }),
    ...FRESHNESS.itemOptions
  });

  const items = [
    ...toOptions(ingredients.data?.listIngredientsByName.data),
    ...toOptions(components.data?.listFoodComponentsByName.data)
  ];
  return {
    items,
    loading: ingredients.isPending || components.isPending,
    error: loadError(ingredients) ?? loadError(components)
  };
};

/** The error to show in place of the data: only a read that never succeeded. A failed refresh of loaded data keeps showing the data. */
const loadError = (query: { isLoadingError: boolean; error: Error | null }): ErrorLike | undefined =>
  query.isLoadingError ? (query.error ?? undefined) : undefined;

const uniqById = (batches: Batch[]): Batch[] => [...new Map(batches.map((batch) => [batch.id, batch])).values()];

export interface UseBatchTrackingBatchesParams extends BatchFilter {
  kitchen: Kitchen;
}

/** `listBatchesV2`: the batches, newest first; `loadMore` fetches the next page with the cursor and appends it to `batches`. */
export interface UseBatchTrackingBatchesResult {
  batches: Batch[];
  loading: boolean;
  error: ErrorLike | undefined;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useBatchTrackingBatches = ({ kitchen, ...filter }: UseBatchTrackingBatchesParams): UseBatchTrackingBatchesResult => {
  const client = useApolloClient();
  const query = useInfiniteQuery(batchesOptions(client, kitchen, filter));
  return {
    batches: uniqById((query.data?.pages ?? []).flatMap((page) => page.items)),
    loading: query.isPending,
    error: loadError(query),
    hasMore: query.hasNextPage,
    loadMore: async () => {
      if (query.hasNextPage) {
        await query.fetchNextPage({ cancelRefetch: false });
      }
    },
    refetch: async () => {
      await query.refetch();
    }
  };
};

export interface UseBatchTrackingBatchParams {
  kitchen: Kitchen;
  /** The route's batch ID; nothing is read while it is undefined. */
  id: string | undefined;
}

/** `getBatchV2`: the batch, its label and the batches made from it, deleted ones too. A failure keeps its AppSync `errorType` (BatchNotFound for an unknown ID). */
export interface UseBatchTrackingBatchResult {
  details: BatchDetails | undefined;
  loading: boolean;
  error: ErrorLike | undefined;
  refetch: () => Promise<void>;
}

export const useBatchTrackingBatch = ({ kitchen, id }: UseBatchTrackingBatchParams): UseBatchTrackingBatchResult => {
  const client = useApolloClient();
  const query = useQuery({ ...batchOptions(client, kitchen, id ?? ''), enabled: id !== undefined });
  return {
    details: query.data,
    loading: query.isPending && query.fetchStatus !== 'idle',
    error: loadError(query),
    refetch: async () => {
      await query.refetch();
    }
  };
};

export interface UseBatchAccountActivityResult {
  activity: BatchAccountActivity | undefined;
  loading: boolean;
  /** Set only when no read of this window has ever succeeded; never shown as zeros. */
  error: ErrorLike | undefined;
  /** The last read failed: `activity` is the last good one, older than `asOf` says it should be. */
  stale: boolean;
  refreshing: boolean;
  refetch: () => Promise<void>;
}

/** `getBatchAccountActivityV2` for kitchen days `from`..`to`; read again every minute while the tab is visible. */
export const useBatchAccountActivity = (kitchen: Kitchen, days: ActivityWindow): UseBatchAccountActivityResult => {
  const client = useApolloClient();
  const query = useQuery(accountActivityOptions(client, kitchen, days));
  return {
    activity: query.data,
    loading: query.isPending,
    error: loadError(query),
    stale: query.isRefetchError,
    refreshing: query.isFetching,
    refetch: async () => {
      await query.refetch();
    }
  };
};

export interface UseHomeBatchesResult {
  batches: Batch[];
  loading: boolean;
  error: boolean;
  /** The server refused the account for this kitchen; a retry changes nothing. */
  unauthorized: boolean;
  reload: () => void;
}

/** `listHomeBatchesV2` for a kitchen day (`yyyy-MM-dd`), newest first: the caller's own, or the kitchen's all for a head chef. Polled while Home is open. */
export const useHomeBatches = (kitchen: Kitchen, day: string): UseHomeBatchesResult => {
  const client = useApolloClient();
  const query = useQuery(homeBatchesOptions(client, kitchen, day));
  return {
    batches: query.data ?? [],
    loading: query.isPending,
    error: query.isLoadingError,
    unauthorized: getAppSyncErrorType(query.error) === 'Unauthorized',
    reload: () => {
      void query.refetch();
    }
  };
};

export interface UseTrackableItemsResult {
  items: TrackableItem[];
  loading: boolean;
  error: boolean;
  /** The server refused the account for this kitchen; a retry changes nothing. */
  unauthorized: boolean;
  reload: () => void;
}

/** `listTrackableItems`: every approved component and ingredient of the kitchen, prefetched when KDS opens and kept fresh for the search. */
export const useTrackableItems = (kitchen: Kitchen): UseTrackableItemsResult => {
  const client = useApolloClient();
  const query = useQuery(trackableItemsOptions(client, kitchen));
  return {
    items: query.data ?? [],
    loading: query.isPending,
    error: query.isLoadingError,
    unauthorized: getAppSyncErrorType(query.error) === 'Unauthorized',
    reload: () => {
      void query.refetch();
    }
  };
};
